import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { createOrg, type Organization, type SigningMode } from './api';
import { SigningModeSelector } from './SigningModeSelector';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Logo } from '../../components/Logo';

export function OnboardingPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [country, setCountry] = useState('');
  const [signingMode, setSigningMode] = useState<SigningMode>('client_side');

  const create = useMutation({
    mutationFn: createOrg,
    onSuccess: async (newOrg) => {
      // Seed the cache directly so the app doesn't depend on the refetch race
      // between invalidate and the next render. Then refresh in the background.
      qc.setQueryData<Organization[]>(['orgs'], (prev) => {
        const list = prev ?? [];
        if (list.some((o) => o.id === newOrg.id)) return list;
        return [newOrg, ...list];
      });
      void qc.invalidateQueries({ queryKey: ['orgs'] });
      navigate('/');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate({
      name,
      slug,
      country: country || undefined,
      signing_mode: signingMode,
    });
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-xl items-center px-6">
      <div className="w-full">
        <div className="mb-8 text-center">
          <div className="mb-6 flex justify-center">
            <Logo variant="lockup" size={36} />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Welcome to Sigil</h1>
          <p className="mt-2 text-sm text-ink-secondary">
            Tell us about your company to finish setting up your account.
          </p>
        </div>
        <Card>
          <CardHeader title="Your company" description="You'll be the owner. You can invite teammates later." />
          <CardBody>
            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="Company name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Inc."
                required
              />
              <Input
                label="Slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder="acme"
                hint="Lowercase letters, numbers and dashes. Used internally."
                required
                pattern="^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$"
              />
              <Input
                label="Country"
                value={country}
                onChange={(e) => setCountry(e.target.value.toUpperCase())}
                placeholder="ES"
                hint="ISO 3166-1 alpha-2."
                maxLength={2}
              />
              <SigningModeSelector value={signingMode} onChange={setSigningMode} />
              {create.isError && (
                <p className="text-sm text-bad">
                  {create.error instanceof Error ? create.error.message : 'Could not create company'}
                </p>
              )}
              <Button type="submit" className="w-full justify-center" disabled={create.isPending}>
                {create.isPending ? 'Creating...' : 'Continue'}
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
